import { SessionContext } from '@/context/SessionContext';
import { Instrument } from '@/javascript/music-objects';
import {
    Accordion,
    Button, FileInput, Group, Loader, Modal, MultiSelect, PasswordInput,
    Slider, Stack, Text, TextInput,
    useMantineTheme
} from '@mantine/core';
import { useForm } from '@mantine/form';
import {
    IconAlertCircle,
    IconCamera,
    IconCheck,
    IconLock,
    IconMail,
    IconMusic,
    IconPassword,
    IconRestore
} from '@tabler/icons-react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import AvatarEditor from 'react-avatar-editor';
import TosPdfViewerModal from './TosPdfViewerModal';

interface UserSettingsModalProps {
    showModal: boolean;
    setShowModal: (show: boolean) => void;
}

const UserSettingsModal: React.FC<UserSettingsModalProps> = ({ showModal, setShowModal }) => {
    const theme = useMantineTheme();
    const session = useContext(SessionContext);
    if (!session) throw new Error("UserSettingsModal must be a child of SessionContext.");

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [scale, setScale] = useState<number>(1.2);
    const [emailConfirmationSent, setEmailConfirmationSent] = useState(false);
    const [instruments, setInstruments] = useState<Instrument[] | null>(null);
    const editorRef = useRef<AvatarEditor | null>(null);
    const [tosViewerOpen, setTosViewerOpen] = useState(false);

    const usernameForm = useForm({
        initialValues: { newUsername: '' },
        validate: {
            newUsername: (value) => value.trim() === '' ? 'Username cannot be empty' : null
        }
    });

    const emailForm = useForm({
        initialValues: { newEmail: '' },
        validate: {
            newEmail: (value) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ? 'Invalid email address' : null
        }
    });

    const passwordForm = useForm({
        initialValues: {
            currentPassword: '',
            newPassword: ''
        },
        validate: {
            currentPassword: (value) => value.trim() === '' ? 'Current password cannot be empty' : null,
            newPassword: (value) => {
                if (value.length < 6 || value.length > 30) return 'Password must be between 6 and 30 characters';
                if (!/[a-z]/.test(value)) return 'Must contain a lowercase letter';
                if (!/[A-Z]/.test(value)) return 'Must contain an uppercase letter';
                if (!/[0-9]/.test(value)) return 'Must contain a number';
                if (!/[^a-z\d]/i.test(value)) return 'Must contain a special character';
                return null;
            }
        }
    });

    const instrumentsForm = useForm({
        initialValues: {
            newInstruments: [] as string[]
        }
    })

    useEffect(() => {
        if (!session.user) {
            setShowModal(false);
            return;
        }
        usernameForm.setValues({ newUsername: session.user.username });
        if (session.user.email) {
            emailForm.setValues({ newEmail: session.user.email });
        }
        instrumentsForm.setValues({ newInstruments: session.user.instruments.map(i => i.name) });
    }, [session.user]);

    const handleUsernameSubmit = useCallback(async ({ newUsername }: typeof usernameForm.values) => {
        const { hasErrors } = usernameForm.validate();
        if (!hasErrors && newUsername !== session.user?.username) {
            setIsSubmitting(true);
            try {
                await session.changeUsername(newUsername);
            } finally {
                setIsSubmitting(false);
            }
        }
    }, [session]);

    const handleEmailSubmit = useCallback(async ({ newEmail }: typeof emailForm.values) => {
        const { hasErrors } = emailForm.validate();
        if (!hasErrors && newEmail !== session.user?.email) {
            setIsSubmitting(true);
            try {
                await session.changeEmail(newEmail);
                setEmailConfirmationSent(true);
            } finally {
                setIsSubmitting(false);
            }
        }
    }, [session]);


    const handleSendEmailConfirmation = useCallback(async () => {
        setIsSubmitting(true);
        try {
            await session.sendEmailConfirmation();
            setEmailConfirmationSent(true);
        } finally {
            setIsSubmitting(false);
        }
    }, [session]);

    const handlePasswordSubmit = useCallback(async ({ currentPassword, newPassword }: typeof passwordForm.values) => {
        const { hasErrors } = passwordForm.validate();
        if (!hasErrors) {
            setIsSubmitting(true);
            try {
                await session.changePassword(currentPassword, newPassword);
                // Clear the form after successful change
                passwordForm.reset();
            } finally {
                setIsSubmitting(false);
            }
        }
    }, [session]);

    const handleInstrumentsSubmit = useCallback(async ({ newInstruments }: typeof instrumentsForm.values) => {
        const { hasErrors } = instrumentsForm.validate();
        if (!hasErrors) {
            setIsSubmitting(true);
            try {
                await session.changeInstruments(newInstruments);
            } finally {
                setIsSubmitting(false);
            }
        }
    }, [session]);

    const handleUploadProfileImage = async () => {
        if (!imageFile || !editorRef.current) return;

        const canvas = editorRef.current.getImageScaledToCanvas();
        canvas.toBlob(async (blob: Blob | null) => {
            if (blob) {
                const file = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
                setIsSubmitting(true);
                try {
                    await session.uploadProfileImage(file);
                    setImageFile(null);
                    setScale(1.2);
                } catch (e: unknown) {
                    console.error("Upload failed:", e);
                } finally {
                    setIsSubmitting(false);
                }
            }
        }, "image/jpeg");
    };

    const handleAgreeToTos = useCallback(async () => {
        if (session.currentTos == null) return;

        setIsSubmitting(true);
        try {
            await session.agreeToTos(session.currentTos.version);
        } finally {
            setIsSubmitting(false);
        }
    }, [session]);

    useEffect(() => {
        setInstruments(session.instruments);
    }, [session.instruments]);

    useEffect(() => {
        if (showModal && session.user?.agreedToCurrentTos === false && session.currentTos == null) {
            session.fetchCurrentTos();
        }
    }, [showModal, session.user?.agreedToCurrentTos, session.currentTos]);

    return (
        <Modal
            opened={showModal}
            onClose={() => setShowModal(false)}
            title="User Settings"
            centered
            size="lg"
            styles={{ title: { fontSize: 26, fontVariant: "all-small-caps" } }}
            withCloseButton
        >
            <Accordion defaultValue={session.user?.emailConfirmed ? "profile-image" : "email"} variant="contained">
                <Accordion.Item value="profile-image">
                    <Accordion.Control>Update Profile Picture</Accordion.Control>
                    <Accordion.Panel>
                        <Stack mt="md">
                            {!imageFile && (
                                <FileInput
                                    accept="image/*"
                                    onChange={(file) => {
                                        if (file) setImageFile(file);
                                    }}
                                    placeholder="Click to select an image"
                                    leftSection={<IconCamera size={16} />}
                                />
                            )}

                            {imageFile && (
                                <>
                                    <AvatarEditor
                                        ref={editorRef}
                                        image={imageFile}
                                        border={50}
                                        borderRadius={100}
                                        scale={scale}
                                        rotate={0}
                                    />
                                    <Slider
                                        min={1}
                                        max={3}
                                        step={0.01}
                                        value={scale}
                                        onChange={(value) => setScale(value)}
                                        label={null}
                                    />
                                    <Group>
                                        <Button onClick={handleUploadProfileImage} loading={isSubmitting} leftSection={<IconCamera size={16} />}>
                                            Upload Image
                                        </Button>
                                        <Button variant="light" color="gray" onClick={() => setImageFile(null)}>
                                            Cancel
                                        </Button>
                                    </Group>
                                </>
                            )}
                        </Stack>
                    </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="email">
                    <Accordion.Control>
                        {session.user?.emailConfirmed ? (
                            <Text>
                                Change Email
                            </Text>
                        ) : (
                            <Group>
                                <Text>
                                    Confirm Email
                                </Text>
                                <IconAlertCircle color={theme.colors.yellow[6]} />
                            </Group>
                        )}
                    </Accordion.Control>
                    <Accordion.Panel>
                        <form onSubmit={emailForm.onSubmit(handleEmailSubmit)}>
                            <Stack mt="sm" w="100%">
                                <TextInput
                                    {...emailForm.getInputProps('newEmail')}
                                    disabled={isSubmitting}
                                    required
                                    withAsterisk={false}
                                    w="100%"
                                    rightSection={
                                        <IconRestore
                                            size={16}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => emailForm.setFieldValue('newEmail', session.user?.email || '')}
                                        />
                                    }
                                />
                                {session.user?.email === emailForm.values.newEmail ? (
                                    <Button
                                        type="button"
                                        loading={isSubmitting}
                                        disabled={session.user.emailConfirmed}
                                        leftSection={
                                            session.user.emailConfirmed
                                                ? <IconCheck size={16} color="green" />
                                                : <IconMail size={16} color="yellow" />
                                        }
                                        onClick={handleSendEmailConfirmation}
                                        w="100%"
                                    >
                                        {session.user.emailConfirmed ? "Email Confirmed" : emailConfirmationSent ? "Send Confirmation Again" : "Send Confirmation Email"}
                                    </Button>
                                ) : (
                                    <Button
                                        type="submit"
                                        loading={isSubmitting}
                                        disabled={
                                            isSubmitting ||
                                            emailForm.values.newEmail.trim() === session.user?.email ||
                                            !/^\S+@\S+\.\S+$/.test(emailForm.values.newEmail)
                                        }
                                        w="100%"
                                    >
                                        Change Email
                                    </Button>
                                )}
                            </Stack>
                        </form>
                    </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="username">
                    <Accordion.Control>Change Username</Accordion.Control>
                    <Accordion.Panel>
                        <form onSubmit={usernameForm.onSubmit(handleUsernameSubmit)}>
                            <Stack mt="sm">
                                <TextInput
                                    {...usernameForm.getInputProps('newUsername')}
                                    disabled={isSubmitting}
                                    required
                                    withAsterisk={false}
                                    w="100%"
                                    rightSection={
                                        <IconRestore
                                            size={16}
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => usernameForm.setFieldValue('newUsername', session.user?.username || '')}
                                        />
                                    }
                                />
                                <Button
                                    type="submit"
                                    loading={isSubmitting}
                                    disabled={usernameForm.values.newUsername.trim() === session.user?.username}
                                    w="100%"
                                >
                                    Change Username
                                </Button>
                            </Stack>
                        </form>
                    </Accordion.Panel>
                </Accordion.Item>

                <Accordion.Item value="password">
                    <Accordion.Control>Change Password</Accordion.Control>
                    <Accordion.Panel>
                        <form onSubmit={passwordForm.onSubmit(handlePasswordSubmit)}>
                            <Stack mt="sm">
                                <PasswordInput
                                    label={<Group><IconLock size={20} /><Text>Current Password</Text></Group>}
                                    {...passwordForm.getInputProps('currentPassword')}
                                    disabled={isSubmitting}
                                    required
                                    withAsterisk={false}
                                />
                                <PasswordInput
                                    label={<Group><IconPassword size={20} /><Text>New Password</Text></Group>}
                                    {...passwordForm.getInputProps('newPassword')}
                                    description="At least 6 characters, including uppercase, lowercase, number, and special character."
                                    disabled={isSubmitting}
                                    required
                                    withAsterisk={false}
                                />
                                <Button
                                    type="submit"
                                    loading={isSubmitting}
                                    disabled={isSubmitting}
                                >
                                    Change Password
                                </Button>
                            </Stack>
                        </form>
                    </Accordion.Panel>
                </Accordion.Item>

                {session.user?.roles.includes("Instrumentalist") && (
                    <Accordion.Item value="instruments">
                        <Accordion.Control>Change Instruments</Accordion.Control>
                        <Accordion.Panel>
                            <form onSubmit={instrumentsForm.onSubmit(handleInstrumentsSubmit)}>
                                <Stack mt="sm">
                                    {instruments == null ? (
                                        <Loader />
                                    ) : (
                                        <>
                                            <MultiSelect
                                                data={instruments.map((instrument) => ({
                                                    value: instrument.name,
                                                    label: instrument.name
                                                }))}
                                                label={
                                                    <Group>
                                                        <IconMusic size={20} />
                                                        <Text>Which instruments do you want to sign up to record for?</Text>
                                                    </Group>
                                                }
                                                {...instrumentsForm.getInputProps('newInstruments')}
                                                required
                                                withAsterisk={false}
                                                disabled={isSubmitting}
                                            />

                                            <Button
                                                type="submit"
                                                loading={isSubmitting}
                                                disabled={instrumentsForm.values.newInstruments.length === session.user.instruments.length && !instrumentsForm.values.newInstruments.some(value => !session.user?.instruments.some(i => i.name === value))}
                                                w="100%"
                                            >
                                                Change Instruments
                                            </Button>
                                        </>
                                    )}
                                </Stack>
                            </form>
                        </Accordion.Panel>
                    </Accordion.Item>
                )}

                {session.user?.agreedToCurrentTos === false && (
                    <Accordion.Item value="tos">
                        <Accordion.Control>
                            <Group>
                                <Text>Agree to Terms of Service</Text>
                                <IconAlertCircle color={theme.colors.yellow[6]} />
                            </Group>
                        </Accordion.Control>
                        <Accordion.Panel>
                            {session.currentTos == null ? (
                                <Loader />
                            ) : (
                                <Stack>
                                    <Text>
                                        Please review the current Terms of Service.
                                    </Text>
                                    
                                    <TosPdfViewerModal
                                        opened={tosViewerOpen}
                                        onClose={() => setTosViewerOpen(false)}
                                    />

                                    <Button variant="light" onClick={() => setTosViewerOpen(true)}>
                                        View Terms (PDF)
                                    </Button>

                                    <Button
                                        onClick={handleAgreeToTos}
                                        loading={isSubmitting}
                                        disabled={isSubmitting}
                                    >
                                        I Agree to these Terms of Service
                                    </Button>
                                </Stack>
                            )}
                        </Accordion.Panel>
                    </Accordion.Item>
                )}
            </Accordion>
        </Modal>
    );
};

export default UserSettingsModal;
